/**
 * EmailReentryModal — Phase 1 / Wave 3 closure patch
 *
 * Replaces the `window.prompt(...)` fallback in `completeMagicLinkIfPresent`
 * for the cross-device / cross-browser magic-link return path. When the user
 * clicks the magic link in a different browser/device than the one they sent
 * it from, `localStorage[puzzly_magic_email]` is empty and we cannot complete
 * the sign-in without re-collecting the email.
 *
 * Architecture:
 *   - `completeMagicLinkIfPresent()` returns `{ status: 'needs_email', url }`
 *     instead of calling `window.prompt`.
 *   - App.jsx renders this modal when that sentinel is returned.
 *   - On submit, the modal calls `completeMagicLinkWithEmail(email, url)`
 *     which signs the user in.
 *
 * Design follows PATTERNS §5: same overlay shell as `HowItWorksModal` /
 * `SignUpPrompt` — rgba(0,0,0,0.5) backdrop + blur(8px), candy_heart card,
 * 2rem radius, theme vars only (no inline brand colors).
 *
 * Props:
 *   - open (bool): controls visibility
 *   - url (string): the magic-link URL captured from the original return (so
 *     the user's address bar may have been replaceState-cleared by the time
 *     this renders).
 *   - onClose (fn): close handler (Cancel button, success completion). The
 *     parent should also clear the `needs_email` state when this fires.
 *   - onSuccess (fn(user)): called after a successful sign-in with the
 *     resulting Firebase User. The parent typically uses this to trigger
 *     post-sign-in side effects (refresh history, dismiss the modal).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  completeMagicLinkWithEmail,
  friendlyAuthError,
} from "../services/authService";

export default function EmailReentryModal({ open, url, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleSubmit = async () => {
    if (!email || !/.+@.+\..+/.test(email)) {
      setError(t("ui.emailReentry.invalidEmail"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const user = await completeMagicLinkWithEmail(email, url);
      if (onSuccess) onSuccess(user);
      if (onClose) onClose();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(8px)",
    zIndex: 330,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  };
  const cardStyle = {
    background: "var(--color-surface)",
    borderRadius: "2rem",
    padding: 32,
    maxWidth: 420,
    width: "100%",
    boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
    position: "relative",
  };
  const closeBtn = {
    position: "absolute",
    top: 16,
    right: 16,
    background: "var(--color-bgAlt)",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "var(--color-textMuted)",
    fontSize: 16,
    fontWeight: 700,
  };
  const ctaPrimary = {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: 16,
    fontFamily: "var(--font-body)",
    fontSize: 16,
    fontWeight: 700,
    cursor: busy ? "not-allowed" : "pointer",
    background:
      "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
    color: "var(--color-onPrimary)",
    boxShadow: "0 8px 24px var(--color-glow)",
    opacity: busy ? 0.7 : 1,
  };
  const ctaSecondary = {
    ...ctaPrimary,
    background: "var(--color-bgAlt)",
    color: "var(--color-text)",
    boxShadow: "none",
    opacity: 1,
  };
  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "none",
    borderRadius: 12,
    background: "var(--color-bgAlt)",
    fontFamily: "var(--font-body)",
    fontSize: 16,
    color: "var(--color-text)",
    boxSizing: "border-box",
    marginBottom: 12,
  };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle}>
        <button onClick={onClose} style={closeBtn} aria-label={t("ui.emailReentry.closeAria")}>
          ✕
        </button>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 20,
            margin: "0 auto 16px",
            background:
              "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            boxShadow: "0 8px 24px var(--color-glow)",
          }}
        >
          ✉️
        </div>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 22,
            fontWeight: 800,
            color: "var(--color-text)",
            margin: "0 0 8px",
            textAlign: "center",
          }}
        >
          {t("ui.emailReentry.title")}
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 14,
            color: "var(--color-textMuted)",
            textAlign: "center",
            margin: "0 0 24px",
            lineHeight: 1.5,
          }}
        >
          {t("ui.emailReentry.body")}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            type="email"
            role="textbox"
            maxLength={254}
            placeholder={t("ui.emailReentry.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !busy) handleSubmit();
            }}
            style={inputStyle}
            autoFocus
            disabled={busy}
          />
          <button onClick={handleSubmit} disabled={busy} style={ctaPrimary}>
            {busy ? t("ui.emailReentry.submitBusy") : t("ui.emailReentry.submit")}
          </button>
          <button onClick={onClose} disabled={busy} style={ctaSecondary}>
            {t("ui.emailReentry.cancel")}
          </button>
        </div>

        {error && (
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 13,
              color: "var(--color-wrong)",
              textAlign: "center",
              margin: "12px 0 0",
            }}
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
